import { kpiApi } from "@/api/kpiApi";
import { groupApi } from "@/store/query/group";
import {
    Box,
    Button,
    Divider,
    FormControl,
    FormHelperText,
    FormLabel,
    MenuItem,
    Stack,
    TextField as MuiTextField,
} from "@mui/material";
import { Field, Form, FormikProvider, useFormik } from "formik";
import { Select, Autocomplete } from "formik-mui";
import { instanceOf } from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import * as yup from "yup";

const defaultOldGroupTypeOptions = [
    {
        id: 1,
        namespace: "KPIs/ResponsibleGroup",
        title: "กลุ่มงาน",
    },
    {
        id: 2,
        namespace: "User/Executive",
        title: "ตำแหน่งบริหาร",
    },
    {
        id: 3,
        namespace: "user-self-group",
        title: "เจ้าหน้าที่",
    },
];

const defaultGroupsFilter = {
    search: "",
    filter: {
        group_namespace: "",
        active: true,
    },
    page: {
        number: 1,
        size: 1000,
    },
};

export function KpiAddNewMemberForm(props) {
    const { kpi, handleAddMember, handleOnCloseForm, ...restProps } = props;
    const memberGroupIds = kpi?.data?.data?.stakeholder_groups?.map((group) => group.id) ?? [];
    const [groupTypeOptions] = useState(defaultOldGroupTypeOptions);
    const [groupsFilter, setGroupsFilter] = useState(defaultGroupsFilter);

    const updateGroupNamespaceFilter = useCallback(
        (value) => {
            setGroupsFilter((prev) => ({
                ...prev,
                filter: { ...prev.filter, group_namespace: value },
            }));
        },
        [setGroupsFilter]
    );
    const updateGroupSearchFilter = useCallback(
        _.debounce((value) => {
            setGroupsFilter((prev) => ({ ...prev, search: value }));
        }, 500),
        [setGroupsFilter]
    );

    // fetch group api.
    const groups = kpiApi.endpoints.getKpiResponsibleGroup.useQuery(groupsFilter);

    // setup formik
    const formik = useFormik({
        initialValues: {
            group_namespace: "",
            group: null,
        },
        validationSchema: yup.object().shape({
            group_namespace: yup.string().required(),
            group: yup.object().required(),
        }),
        // checking.
        onSubmit: async (values, formikBag) => {
            await handleAddMember(values.group.id);
            // NOTE: this function will call handleOnCloseForm() twice!!
            formikBag.resetForm();
        },
        onReset: () => {
            // callback
            if (handleOnCloseForm instanceof Function) {
                handleOnCloseForm();
            }
        },
    });
    const { touched, errors } = formik;

    return (
        <FormikProvider value={formik}>
            <Form>
                <Stack spacing={3}>
                    <FormControl fullWidth>
                        <FormLabel required>Group type</FormLabel>
                        <Field
                            component={Select}
                            name="group_namespace"
                            required
                            onChange={(event) => {
                                updateGroupNamespaceFilter(event.target.value ?? "");
                                // clear selected group.
                                formik.setFieldValue("group", null);
                            }}
                        >
                            {groupTypeOptions?.map((groupTypeOption) => (
                                <MenuItem key={groupTypeOption.id} value={groupTypeOption.namespace}>
                                    {groupTypeOption.title}
                                </MenuItem>
                            ))}
                        </Field>
                        <FormHelperText name="group_namespace" touched={touched} errors={errors} />
                    </FormControl>

                    <FormControl fullWidth>
                        <FormLabel required>Group</FormLabel>
                        <Field
                            // ref={resetSelectGroup}
                            component={Autocomplete}
                            name="group"
                            options={groups.data?.data ?? []}
                            loading={groups.isFetching}
                            isOptionEqualToValue={(option, value) => option.id === value?.id}
                            getOptionLabel={(option) => option.title}
                            getOptionDisabled={(option) => memberGroupIds.includes(option.id)} // style={{ width: 300 }}
                            renderInput={(params) => <MuiTextField {...params} label="ค้นหา" variant="outlined" />}
                            renderOption={(props, option) => (
                                <MenuItem {...props} key={option.id}>
                                    {option.title}
                                </MenuItem>
                            )}
                            onInputChange={(event, value) => updateGroupSearchFilter(value)}
                        />
                        <FormHelperText name="group" touched={touched} errors={errors} />
                    </FormControl>

                    <Box sx={{ display: "flex" }}>
                        <Button type="submit" variant="contained" disabled={formik.isSubmitting || !formik.dirty}>
                            Save
                        </Button>
                        <Button type="reset" disabled={formik.isSubmitting}>
                            Cancel
                        </Button>
                    </Box>
                </Stack>
            </Form>
        </FormikProvider>
    );
}
